'use client';

/*
 * KARAMA YOGA, Landing Page
 * Design: bright white canvas, MWH + Sculpt Society inspired. Brand colors
 * (teal/purple/pink) are accents only, never full-page backgrounds.
 *
 * Live data (not the design's mock constants):
 *   - Schedule  → get_available_classes_for_date RPC, deep-linked to /event/[id]
 *   - Pricing   → booking_packages, deep-linked to /payment/package/[id] or /book
 * Everything below the schedule mirrors the approved Manus design.
 */

import { useCallback, useEffect, useRef, useState } from 'react';

import Image from 'next/image';
import Link from '~/(tenant)/_components/tenant-link';

import { useQuery } from '@tanstack/react-query';
import {
  AnimatePresence,
  motion,
  useReducedMotion,
  useScroll,
  useSpring,
  useTransform,
} from 'framer-motion';
import {
  ArrowRight,
  Award,
  Baby,
  Book,
  Briefcase,
  CalendarDays,
  ChevronRight,
  Clock,
  ExternalLink,
  Flame,
  Globe,
  Heart,
  Leaf,
  MapPin,
  MessageCircle,
  Sparkles,
  Star,
} from 'lucide-react';

import { useTranslation } from 'react-i18next';

import { useSupabase } from '@kit/supabase/hooks/use-supabase';

import type { LandingPageProps } from '../_lib/landing-page.types';
import { PricingSection } from './components/pricing-section';
import './styles.css';

// Bilingual copy is kept inline (EN + AR) rather than in the shared
// `landing.json` namespace, matching the netacademy landing page and the
// header's FOUNDER_NAME constant: tenant-specific marketing copy stays
// self-contained so it never pollutes the global keyspace. `isAr` is derived
// from `i18n.language` in each section; RTL direction flips automatically from
// the <html dir> set in app/layout.tsx when the locale is `ar`.
type Bilingual = { en: string; ar: string };
const pick = (s: Bilingual, isAr: boolean) => (isAr ? s.ar : s.en);

const WHATSAPP_URL = 'https://wa.me/966508686446';
// Hardcoded studio location (Google Maps share link from the Karama Yoga
// Google Business Profile). Used by the Location section's Address card.
const MAPS_URL = 'https://maps.app.goo.gl/ae1bwbTLSXwGFLyw8';

const IMAGES = {
  // Hero carousel: the two real studio photos (mats laid out, indoor/outdoor
  // glass entrance with the lit Karama wordmark).
  heroMats: '/landing-pages/karama/images/hero-mat-shots.png',
  heroEntrance: '/landing-pages/karama/images/hero-indoor-outdoor.png',
  // Retained studio photography still referenced by gallery/training sections.
  heroWall: '/landing-pages/karama/images/hero-studio-wall.jpg',
  // Content imagery.
  about: '/landing-pages/karama/images/about.jpg',
  boutique: '/landing-pages/karama/images/boutique.jpg',
  exterior: '/landing-pages/karama/images/exterior-new.jpg',
  founder: '/landing-pages/karama/images/founder.jpg',
  founder2: '/landing-pages/karama/images/founder2.jpg',
  // Class cards.
  classFlow: '/landing-pages/karama/images/cls-ashtanga.jpg',
  classWarrior: '/landing-pages/karama/images/cls-sweaty-warrior.jpg',
  classRestore: '/landing-pages/karama/images/cls-stress-release.jpg',
  classMoon: '/landing-pages/karama/images/cls-candlelight.jpg',
} as const;

const CLASS_IMAGE_BASE = '/landing-pages/karama/images';

// Fallback class photography, matched by keyword in the class name. Used when a
// session has no DB image_url so every schedule card still shows a relevant
// photo instead of a flat colour panel. First matching keyword wins; the list
// is ordered most-specific first so e.g. "Aerial Yoga" matches before "Yoga".
const CLASS_IMAGE_KEYWORDS: ReadonlyArray<readonly [string, string]> = [
  ['sweaty warrior', `${CLASS_IMAGE_BASE}/cls-sweaty-warrior.jpg`],
  ['warrior', `${CLASS_IMAGE_BASE}/cls-sweaty-warrior.jpg`],
  ['sweaty core', `${CLASS_IMAGE_BASE}/cls-sweaty-core.jpg`],
  ['core', `${CLASS_IMAGE_BASE}/cls-sweaty-core.jpg`],
  ['power pilates', `${CLASS_IMAGE_BASE}/cls-power-pilates.jpg`],
  ['pilates', `${CLASS_IMAGE_BASE}/cls-power-pilates.jpg`],
  ['aerial stretch', `${CLASS_IMAGE_BASE}/cls-aerial-stretch.jpg`],
  ['aerial', `${CLASS_IMAGE_BASE}/cls-aerial-yoga.jpg`],
  ['sculpt', `${CLASS_IMAGE_BASE}/cls-sculpt-flow.jpg`],
  ['yoga sculpt', `${CLASS_IMAGE_BASE}/cls-yoga-sculpt.jpg`],
  ['mobility', `${CLASS_IMAGE_BASE}/cls-mobility.jpg`],
  ['stretch', `${CLASS_IMAGE_BASE}/cls-mobility.jpg`],
  ['ashtanga', `${CLASS_IMAGE_BASE}/cls-ashtanga.jpg`],
  ['mama', `${CLASS_IMAGE_BASE}/cls-karama-mama.jpg`],
  ['prenatal', `${CLASS_IMAGE_BASE}/cls-karama-mama.jpg`],
  ['candlelight', `${CLASS_IMAGE_BASE}/cls-candlelight.jpg`],
  ['candle', `${CLASS_IMAGE_BASE}/cls-candlelight.jpg`],
  ['moon', `${CLASS_IMAGE_BASE}/cls-candlelight.jpg`],
  ['restore', `${CLASS_IMAGE_BASE}/cls-stress-release.jpg`],
  ['stress', `${CLASS_IMAGE_BASE}/cls-stress-release.jpg`],
  ['release', `${CLASS_IMAGE_BASE}/cls-stress-release.jpg`],
  ['hatha', `${CLASS_IMAGE_BASE}/cls-stress-release.jpg`],
  ['nidra', `${CLASS_IMAGE_BASE}/cls-candlelight.jpg`],
  ['flow', `${CLASS_IMAGE_BASE}/cls-sculpt-flow.jpg`],
  ['vinyasa', `${CLASS_IMAGE_BASE}/cls-sculpt-flow.jpg`],
  ['yoga', `${CLASS_IMAGE_BASE}/cls-yoga-sculpt.jpg`],
];

// Cycled when no keyword matches, so generic class names still vary by card.
const CLASS_IMAGE_FALLBACKS = [
  `${CLASS_IMAGE_BASE}/cls-ashtanga.jpg`,
  `${CLASS_IMAGE_BASE}/cls-sculpt-flow.jpg`,
  `${CLASS_IMAGE_BASE}/cls-mobility.jpg`,
] as const;

/** Resolve a fallback class photo by keyword, cycling a default otherwise. */
function classImageFor(name: string, index: number) {
  const lower = name.toLowerCase();
  for (const [keyword, src] of CLASS_IMAGE_KEYWORDS) {
    if (lower.includes(keyword)) return src;
  }
  return CLASS_IMAGE_FALLBACKS[index % CLASS_IMAGE_FALLBACKS.length]!;
}

const EASE_OUT = [0.23, 1, 0.32, 1] as const;

// Reveal variants. A single, short opacity-only fade on the SECTION container
// (driven by the parent's `stagger`/`whileInView`), no per-child transform or
// blur. Animating opacity only, once, on the wrapper avoids the "load then
// reload" flicker that came from dozens of child elements each running their
// own in-view transform on momentum scroll. Children inherit `visible`
// instantly via the stagger parent, so they never sit at opacity 0.
// Reveals disabled, see note above. These remain as no-op variants so the
// existing variants={fadeUp}/{stagger} props stay valid without animating.
const fadeUp = { hidden: { opacity: 1 }, visible: { opacity: 1 } };
const stagger = { hidden: { opacity: 1 }, visible: { opacity: 1 } };

// Two-slide hero carousel using the real studio photography: the mat-shot
// interior and the indoor/outdoor glass entrance.
const HERO_SLIDES = [
  {
    src: IMAGES.heroMats,
    label: { en: 'Jeddah · Est. 2016', ar: 'جدة · منذ ٢٠١٦' },
    lines: {
      en: ['Not just a Studio.', 'An Experience.', 'A Feeling. A Shift.'],
      ar: ['ليس مجرد استوديو', 'بل تجربة', 'شعور. تحوّل'],
    },
  },
  {
    src: IMAGES.heroEntrance,
    label: { en: 'The Karama Yoga Method', ar: 'أسلوب كراما في اليوغا' },
    lines: {
      en: ['Sweat', 'Stretch', '& Feel Your Best'],
      ar: ['قوة. انسياب', 'توافق', 'تحوّل'],
    },
  },
] as const;

/**
 * Image that drifts vertically as it scrolls through the viewport, creating a
 * subtle parallax depth effect. The image is rendered taller than its frame so
 * the drift never exposes an edge. Disabled entirely under reduced-motion.
 */
function ParallaxImage({
  src,
  alt,
  sizes,
  objectPosition = 'center',
  travel = 60,
  priority = false,
}: {
  src: string;
  alt: string;
  sizes: string;
  objectPosition?: string;
  /** Total vertical drift in px across the scroll range. */
  travel?: number;
  priority?: boolean;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const reduce = useReducedMotion();
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ['start end', 'end start'],
  });
  const y = useTransform(scrollYProgress, [0, 1], [-travel, travel]);

  return (
    <div ref={ref} className="absolute inset-0 overflow-hidden">
      <motion.div
        className="absolute inset-x-0"
        style={
          reduce ? { top: 0, bottom: 0 } : { top: -travel, bottom: -travel, y }
        }
      >
        <Image
          src={src}
          alt={alt}
          fill
          sizes={sizes}
          priority={priority}
          className="object-cover"
          style={{ objectPosition }}
        />
      </motion.div>
    </div>
  );
}

// Compact stat row shown inside "What You'll Find", the page's single source
// of these headline numbers (the standalone stats bar and the duplicate
// icon-card stats were removed in favour of this one row).
const stats: { value: string; label: Bilingual; color: string }[] = [
  {
    value: '10+',
    label: { en: 'Years in Jeddah', ar: 'سنوات في جدة' },
    color: 'var(--karama-teal)',
  },
  {
    value: '13,000+',
    label: { en: 'Lives Impacted', ar: 'حياة تغيّرت' },
    color: 'var(--karama-purple)',
  },
  {
    value: '6×',
    label: { en: 'Prestige Award', ar: 'جائزة بريستيج' },
    color: 'var(--karama-pink)',
  },
  {
    value: '50+',
    label: { en: 'Classes a Week', ar: 'كلاساً في الأسبوع' },
    color: 'var(--karama-teal)',
  },
];

// "What You'll Find at Karama", icon cards on alternating teal/purple tinted
// backgrounds. Replaces both the old plain bullet list and the duplicate
// stat icon-cards.
const whatYoullFind: {
  icon: typeof Heart;
  title: Bilingual;
  tint: 'teal' | 'purple' | 'pink';
}[] = [
  {
    icon: Heart,
    title: { en: 'A community that knows your name', ar: 'مجتمع يعرف اسمكم' },
    tint: 'teal',
  },
  {
    icon: Flame,
    title: {
      en: 'Classes that challenge and inspire',
      ar: 'كلاسات تتحدى وتُلهم',
    },
    tint: 'purple',
  },
  {
    icon: Leaf,
    title: { en: 'A space that feels like home', ar: 'مكان يُشعركم بأنه بيتكم' },
    tint: 'pink',
  },
  {
    icon: Globe,
    title: { en: 'Arabic & English classes', ar: 'كلاسات بالعربي والإنجليزي' },
    tint: 'teal',
  },
  {
    icon: Sparkles,
    title: {
      en: 'Heated, infrared & non-heated options',
      ar: 'خيارات بحرارة وإنفراريد وبدون حرارة',
    },
    tint: 'purple',
  },
  {
    icon: Baby,
    title: {
      en: 'Programs for every age & stage of life',
      ar: 'برامج لكل عمر ومرحلة',
    },
    tint: 'pink',
  },
  {
    icon: CalendarDays,
    title: {
      en: 'Retreats, workshops, trainings & experiences',
      ar: 'رحلات وورش عمل وبرامج تدريبية وتجارب',
    },
    tint: 'teal',
  },
  {
    icon: Award,
    title: {
      en: 'Premium experience, accessible pricing',
      ar: 'تجربة متميزة بأسعار في متناول الجميع',
    },
    tint: 'purple',
  },
];

// Tinted card backgrounds + accent colours for the What You'll Find cards.
// Three-colour brand rotation (teal / purple / pink).
const TINTS = {
  teal: {
    bg: 'color-mix(in srgb, var(--karama-teal) 8%, white)',
    border: 'color-mix(in srgb, var(--karama-teal) 22%, white)',
    accent: 'var(--karama-teal)',
  },
  purple: {
    bg: 'color-mix(in srgb, var(--karama-purple) 8%, white)',
    border: 'color-mix(in srgb, var(--karama-purple) 22%, white)',
    accent: 'var(--karama-purple)',
  },
  pink: {
    bg: 'color-mix(in srgb, var(--karama-pink) 8%, white)',
    border: 'color-mix(in srgb, var(--karama-pink) 22%, white)',
    accent: 'var(--karama-pink)',
  },
} as const;

// Featured-in publications. `href` is optional: pills with a link become
// anchors that open in a new tab; the rest render as static pills.
// `name` doubles as the React key, so outlets covered more than once carry a
// short qualifier to stay unique and to tell the pieces apart.
const MEDIA: ReadonlyArray<{ name: string; href?: string }> = [
  // Linked coverage.
  { name: 'Al Riyadh', href: 'https://www.alriyadh.com/2040406' },
  {
    name: 'Al Midan',
    href: 'https://twitter.com/midanalyaum/status/1718263960472887689',
  },
  {
    name: 'Arab News · Biggest Yoga Class',
    href: 'https://www.arabnews.com/node/2522046/saudi-arabia',
  },
  {
    name: 'Arab News · Woman on a Mission',
    href: 'https://www.arabnews.com/node/2457211/saudi-arabia',
  },
  {
    name: 'Arab News · Yoga Award',
    href: 'https://www.arabnews.com/node/2611544/amp',
  },
  {
    name: 'Scene Now',
    href: 'https://www.scenenow.com/Society/Meet-The-Woman-Behind-the-Kingdom-s-First-Luxury-Infrared-Yoga-Studio',
  },
  {
    name: 'Sayidaty Magazine',
    href: 'https://magazine.sayidaty.net/books/SAYIDATY-MARCH-2026-M/#p=146',
  },
  {
    name: 'Asdaf News',
    href: 'https://ar.asdafnews.com/جدة-تشهد-أكبر-تجمع-لممارسي-ومحبي-رياضة/',
  },
  {
    name: 'Layali Riyadh',
    href: 'https://layalialriyadh.com/جدة-تشهد-أكبر-تجمع-لممارسي-ومحبي-رياضة/',
  },
  {
    name: '3a2ilati',
    href: 'https://www.instagram.com/p/DI1OclXihSI/',
  },
  {
    name: 'Hia Magazine · IG',
    href: 'https://www.instagram.com/p/DNxlna_WGfc/',
  },
  { name: 'Tabaqat', href: 'https://youtu.be/JptlHDwgiYI' },
  {
    name: 'Al Arabiya',
    href: 'https://www.instagram.com/reel/DQNDsxXCo6t/',
  },
  {
    name: 'Mojeh',
    href: 'https://www.mojeh.com/article/10-minutes-with-lana-nazer-karama-yoga/',
  },
  { name: 'Mo Show Podcast', href: 'https://youtu.be/tq8AU6nMLUY' },
  {
    name: 'MBC',
    href: 'https://x.com/MBCinaWeek/status/1961756768150642995',
  },
  {
    name: 'The Gulf Entrepreneur',
    href: 'https://thegulfentrepreneur.com/lana-nazer-founder-of-karama-yoga/',
  },
  { name: 'Gulf Tourism', href: 'https://gulf-tourism.net/?p=80541' },
  {
    name: 'Hia Magazine · Love as a Journey',
    href: 'https://www.hiamag.com/لايف-ستايل/صحة-ورشاقة/1778893-مدربة-اليوغا-لنا-ناظر-تكتب-لـهي-الحب-كرحلة-تعبير-عن-الذات-وتواصل-من',
  },
  {
    name: 'List · Mother-Daughter Duo',
    href: 'https://www.listmag.com/en/see-and-do/adventure-sports/finding-balance-how-this-mother-daughter-duo-conquers-their-fitness-and-life-goals-together-3070',
  },
  {
    name: 'Elle Arabia',
    href: 'https://www.instagram.com/p/C5Y8Booon4y/',
  },
  {
    name: 'Sayidaty · International Yoga Day',
    href: 'https://www.sayidaty.net/تطوير-الذات-والتمكين/قصص-ملهمة/1814459-في-اليوم-العالمي-لليوغا-لنا-ناظرلسيدتي-اليوغا-رحلة-فريدة-نحو',
  },
  {
    name: 'Al Bilad',
    href: 'https://albiladdaily.com/2024/02/29/مدربة-اليوغا-لنا-ناظر-زيادة-الوعي-ب/',
  },
  {
    name: 'Marie Claire',
    href: 'https://www.instagram.com/reel/C3poeiiP5bg/',
  },
];

const philosophy: { title: Bilingual; body: Bilingual }[] = [
  {
    title: { en: 'Community', ar: 'المجتمع' },
    body: {
      en: 'From the moment you walk in, you feel:\nSeen. Welcomed. Supported.',
      ar: 'منذ اللحظة الأولى التي تدخلون فيها، تشعرون بأنكم مرئيون ومرحَّب بكم ومدعومون.',
    },
  },
  {
    title: { en: 'Philosophy', ar: 'الفلسفة' },
    body: {
      en: "We don't push the body. We guide, explore, and transform.",
      ar: 'لا نُرهق الجسم. بل نُرشد ونستكشف ونُحوّل.',
    },
  },
  {
    title: { en: 'Mission', ar: 'الرسالة' },
    body: {
      en: 'Our dream is for every person in Saudi Arabia to experience yoga at least once.',
      ar: 'حلمنا أن يجرّب كل شخص في المملكة العربية السعودية اليوغا مرةً واحدة على الأقل.',
    },
  },
];

const testimonials: {
  quote: Bilingual;
  name: Bilingual;
  role: Bilingual;
  color: string;
}[] = [
  {
    quote: {
      en: "Let's start with the fact that I really, really used to hate yoga! All of that has changed the day I met Lana. She gave me a very different perspective toward yoga and completely changed my mind.",
      ar: 'خلّوني أبدأ بأني كنت أكره اليوغا مرّة! لكن كل هذا تغيّر يوم قابلت لنا. عطتني نظرة مختلفة تمامًا لليوغا وغيّرت فكرتي عنها من الأساس.',
    },
    name: { en: 'Eman Bukhari', ar: 'إيمان بخاري' },
    role: { en: 'Member', ar: 'عضوة' },
    color: 'var(--karama-teal)',
  },
  {
    quote: {
      en: 'Karama has guided me in yoga but also through healthier living; the support and knowledge is always available, making it easier to commit to a healthy lifestyle.',
      ar: 'كراما ما وجّهتني بس في اليوغا، وجّهتني لحياة أصح بشكل عام. الدعم والمعرفة كانوا دائماً متوفرين، وهذا سهّل عليّ الالتزام بأسلوب حياة صحي.',
    },
    name: { en: 'Rasha Al-Sulaiman', ar: 'رشا السليمان' },
    role: { en: 'Member', ar: 'عضوة' },
    color: 'var(--karama-purple)',
  },
  {
    quote: {
      en: "Coming from a high-intensity background, I always struggled to join any yoga class, until I joined Lana's classes. The Sweaty Warrior gave me that invigorating, fast-paced fix while challenging my strength, stamina, and flexibility all at once.",
      ar: 'بحكم خلفيتي في التمارين عالية الشدّة، كان صعب عليّ أتقبّل أي كلاس يوغا. لكن لما جرّبت كلاسات لنا تغيّر كل شيء. كلاس سويتي ووريور عطاني نفس الإحساس الحماسي والسريع اللي أحبّه، مع تحدّي حقيقي لقوتي وتحمّلي ومرونتي.',
    },
    name: { en: 'Ruba Bahareth', ar: 'ربى بحارث' },
    role: { en: 'Member', ar: 'عضوة' },
    color: 'var(--karama-pink)',
  },
  {
    quote: {
      en: 'I told myself this was the perfect opportunity to make a change, so I joined Karama. I wanted to lose weight and find peace of mind, and I found all of that, and so much more.',
      ar: 'قلت لنفسي: هذي فرصتي إني أتغير. وانضمّيت لكراما وأنا هدفي أخفف وزني وألقى راحة بال، ولقيت هذا كله وأكثر بكثير.',
    },
    name: { en: 'Moothy Al-Nwaisir', ar: 'موثى النويصر' },
    role: { en: 'Member', ar: 'عضوة' },
    color: 'var(--karama-teal)',
  },
];

const corporate: {
  icon: typeof Briefcase;
  title: Bilingual;
  desc: Bilingual;
  items: Bilingual[];
  color: string;
}[] = [
  {
    icon: Briefcase,
    title: { en: 'Corporate Wellness', ar: 'صحة الشركات' },
    desc: {
      en: "Support your team's wellbeing through experiences designed to reduce stress, improve morale, and encourage healthier lifestyles.",
      ar: 'ادعموا صحة فريقكم من خلال تجارب مصمَّمة لتخفيف الضغط وتعزيز الروح المعنوية وتشجيع أسلوب حياة أكثر صحة.',
    },
    items: [
      { en: 'Workplace yoga classes', ar: 'كلاسات اليوغا في بيئة العمل' },
      { en: 'Team-building experiences', ar: 'تجارب بناء الفريق' },
      { en: 'Wellness weeks & days', ar: 'أسابيع وأيام العافية' },
      {
        en: 'Conference & event activations',
        ar: 'تفعيلات المؤتمرات والفعاليات',
      },
      { en: 'Employee wellbeing programs', ar: 'برامج صحة الموظفين' },
      { en: 'Private group classes', ar: 'كلاسات جماعية خاصة' },
    ],
    color: 'var(--karama-purple)',
  },
  {
    icon: Sparkles,
    title: { en: 'Brand Partnerships', ar: 'شراكات العلامات التجارية' },
    desc: {
      en: 'We love partnering with brands that share our commitment to community, wellbeing, movement, and meaningful experiences.',
      ar: 'نسعد بالشراكة مع العلامات التجارية التي تشاركنا التزامنا بالمجتمع والعافية والحركة والتجارب الهادفة.',
    },
    items: [
      { en: 'Brand activations', ar: 'تفعيلات العلامات التجارية' },
      { en: 'Product launches', ar: 'إطلاق المنتجات' },
      { en: 'Influencer & media events', ar: 'فعاليات المؤثرين والإعلام' },
      { en: 'Co-branded campaigns', ar: 'حملات مشتركة' },
      { en: 'Sponsored classes & workshops', ar: 'كلاسات وورش عمل مدعومة' },
      { en: 'Creative content creation', ar: 'إنتاج محتوى إبداعي' },
    ],
    color: 'var(--karama-teal)',
  },
  {
    icon: Leaf,
    title: { en: 'Experiences', ar: 'التجارب' },
    desc: {
      en: 'Karama Yoga creates memorable wellness experiences designed for connection, exploration, and transformation.',
      ar: 'تُبدع كراما يوغا في تصميم تجارب صحية لا تُنسى، مبنيّة على التواصل والاستكشاف والتحوّل.',
    },
    items: [
      { en: 'Wellness retreats', ar: 'رحلات صحية' },
      { en: 'Hotel & resort partnerships', ar: 'شراكات الفنادق والمنتجعات' },
      { en: 'Destination experiences', ar: 'تجارب الوجهات' },
      { en: 'Wellness weekends', ar: 'عطل نهاية الأسبوع الصحية' },
      { en: 'Private group retreats', ar: 'رحلات جماعية خاصة' },
      { en: 'Cultural wellness activations', ar: 'تفعيلات الصحة الثقافية' },
    ],
    color: 'var(--karama-purple)',
  },
];

const thisMonth: { icon: typeof Leaf; label: Bilingual; desc: Bilingual }[] = [
  {
    icon: Leaf,
    label: { en: 'Retreats', ar: 'رحلات' },
    desc: {
      en: 'Destination wellness experiences in Saudi Arabia and beyond.',
      ar: 'تجارب صحية في وجهات داخل المملكة وخارجها.',
    },
  },
  {
    icon: CalendarDays,
    label: { en: 'Events', ar: 'فعاليات' },
    desc: {
      en: 'Community gatherings, celebrations, and special sessions.',
      ar: 'تجمعات مجتمعية واحتفالات وكلاسات خاصة.',
    },
  },
  {
    icon: Book,
    label: { en: 'Workshops', ar: 'ورش عمل' },
    desc: {
      en: 'Deep-dive sessions on breathwork, alignment, and mindfulness.',
      ar: 'كلاسات متعمّقة في التنفس والتوافق والتأمّل.',
    },
  },
];

function HeroSection() {
  const ref = useRef<HTMLElement>(null);
  const reduce = useReducedMotion();
  const { i18n } = useTranslation();
  const isAr = i18n.language?.startsWith('ar') ?? false;
  const [slide, setSlide] = useState(0);
  const [dragStart, setDragStart] = useState(0);

  const next = useCallback(
    () => setSlide((s) => (s + 1) % HERO_SLIDES.length),
    [setSlide],
  );
  const prev = useCallback(
    () => setSlide((s) => (s - 1 + HERO_SLIDES.length) % HERO_SLIDES.length),
    [setSlide],
  );

  // Auto-advance every 6s, paused under reduced-motion.
  useEffect(() => {
    if (reduce) return;
    const timer = setInterval(next, 6000);
    return () => clearInterval(timer);
  }, [next, reduce]);

  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ['start start', 'end start'],
  });
  // Content lifts and fades as the visitor scrolls past the hero.
  const contentY = useTransform(scrollYProgress, [0, 1], ['0%', '-42%']);
  const contentOpacity = useTransform(scrollYProgress, [0, 0.55], [1, 0]);

  const active = HERO_SLIDES[slide]!;

  return (
    <section
      ref={ref}
      className="relative overflow-hidden select-none"
      style={{ height: '100vh', minHeight: '680px' }}
      onTouchStart={(e) => setDragStart(e.touches[0]!.clientX)}
      onTouchEnd={(e) => {
        const diff = dragStart - e.changedTouches[0]!.clientX;
        if (Math.abs(diff) > 40) (diff > 0 ? next : prev)();
      }}
    >
      {/* Slides, crossfade with a slow zoom-in. */}
      <AnimatePresence mode="sync">
        <motion.div
          key={slide}
          className="absolute inset-0"
          initial={{ opacity: 0, scale: 1.06 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: reduce ? 0 : 1, ease: EASE_OUT }}
        >
          <Image
            src={active.src}
            alt="Karama Yoga studio in Jeddah"
            fill
            priority={slide === 0}
            sizes="100vw"
            className="object-cover object-center"
          />
        </motion.div>
      </AnimatePresence>

      {/* Top scrim, keeps the transparent navbar's white logo/links legible. */}
      <div
        className="absolute inset-x-0 top-0 h-40"
        style={{
          background:
            'linear-gradient(to bottom, rgba(0,0,0,0.38) 0%, rgba(0,0,0,0.12) 55%, transparent 100%)',
        }}
      />

      {/* Bottom gradient for text legibility. */}
      <div
        className="absolute inset-x-0 bottom-0"
        style={{
          height: '60%',
          background:
            'linear-gradient(to top, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0.1) 60%, transparent 100%)',
        }}
      />

      <motion.div
        className="absolute inset-x-0 bottom-0 mx-auto max-w-[1320px] px-6 pb-12 lg:px-8 lg:pb-16"
        style={reduce ? undefined : { y: contentY, opacity: contentOpacity }}
      >
        <AnimatePresence mode="wait">
          <motion.div
            key={slide}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.6, ease: EASE_OUT }}
            className="max-w-2xl"
          >
            <p className="font-karama-label mb-4 text-xs text-white/85">
              {pick(active.label, isAr)}
            </p>
            <h1
              className="font-karama-heading mb-6 text-white"
              style={{ fontSize: 'clamp(2.4rem, 5.5vw, 5rem)' }}
            >
              {(isAr ? active.lines.ar : active.lines.en).map((line, i) => (
                <span key={i} className="block italic">
                  {line}
                </span>
              ))}
            </h1>
            <div className="flex flex-wrap gap-3">
              <a href="#schedule" className="k-btn-teal">
                {isAr ? 'انظروا إلى الجدول' : 'See the Schedule'}{' '}
                <ChevronRight size={14} />
              </a>
              <a href="#about" className="k-btn-white">
                {isAr ? 'قصتنا' : 'Our Story'}
              </a>
            </div>
          </motion.div>
        </AnimatePresence>
      </motion.div>

      {/* Dot indicators. */}
      <div className="absolute right-6 bottom-6 flex gap-2">
        {HERO_SLIDES.map((s, i) => (
          <button
            key={s.label.en}
            onClick={() => setSlide(i)}
            aria-label={`Slide ${i + 1}`}
            className="h-2 rounded-full transition-all duration-300"
            style={{
              width: i === slide ? '24px' : '8px',
              background: i === slide ? 'var(--karama-teal)' : 'rgba(255,255,255,0.5)',
            }}
          />
        ))}
      </div>
    </section>
  );
}

/**
 * Live "Upcoming classes" schedule, mirrors the native home screen's
 * today-classes logic (same get_available_classes_for_date RPC, next 12
 * sessions, hidden when empty) but rendered in the Karama landing aesthetic.
 * Bookable in one tap: each card deep-links to /event/[booking_id].
 */
function ScheduleSection({ slug }: { slug: string }) {
  const client = useSupabase();
  const { i18n } = useTranslation();
  const isAr = i18n.language?.startsWith('ar') ?? false;

  const { data: classes, isLoading } = useQuery({
    queryKey: ['karama-upcoming-classes', slug],
    queryFn: async () => {
      // Compute the date window inside the query (not during render) so the
      // render stays pure, see react-hooks/purity.
      const now = Date.now();
      const today = new Date(now).toISOString().split('T')[0]!;
      const endDate = new Date(now + 7 * 86_400_000)
        .toISOString()
        .split('T')[0]!;

      const { data } = await client.rpc('get_available_classes_for_date', {
        p_account_slug: slug,
        p_date: today,
        p_end_date: endDate,
      });
      return (data ?? []).slice(0, 12);
    },
    staleTime: 60_000,
    enabled: !!slug,
  });

  // Hide entirely while loading-empty or when there's nothing upcoming -
  // the page flows without a gap (same behaviour as the native section).
  if (isLoading || !classes || classes.length === 0) return null;

  return (
    <section
      id="schedule"
      className="bg-white py-16 lg:py-24"
    >
      <div className="mx-auto max-w-[1320px] px-6 lg:px-8">
        {/* Header carries the Karama Yoga Method brand story, the static
            "class types" grid is merged into this single live section. */}
        <motion.div
          variants={stagger}
          className="mb-10 flex flex-wrap items-end justify-between gap-4"
        >
          <div>
            <motion.p
              variants={fadeUp}
              className="font-karama-label mb-3 text-xs"
              style={{ color: 'var(--karama-purple)' }}
            >
              {isAr ? 'طريقة كراما لليوغا' : 'The Karama Yoga Method'}
            </motion.p>
            <motion.p
              variants={fadeUp}
              className="mt-1 text-sm text-gray-500"
            >
              {isAr
                ? 'القوة من الأشتانغا. الانسياب من الفينياسا. التوافق من الهاثا. كل كلاس متاح للحجز بضغطة واحدة.'
                : 'Strength from Ashtanga. Flow from Vinyasa. Alignment from Hatha. Every session below is live and bookable in one tap.'}
            </motion.p>
          </div>
          <motion.div variants={fadeUp}>
            <Link href="/book" className="k-btn-outline">
              {isAr ? 'انظروا إلى الجدول الكامل' : 'View Full Schedule'}
            </Link>
          </motion.div>
        </motion.div>
      </div>

      {/* Horizontal scroller, compact native-sized cards so several are
          visible at once with a peek of the next. Aligns with the header. */}
      <div className="px-6 lg:px-8">
        <motion.div
          transition={{ duration: 0.6, ease: EASE_OUT }}
          className="karama-scroller mx-auto flex max-w-[1320px] gap-4 overflow-x-auto pb-4"
        >
          {classes.map((cls, i) => (
            <UpcomingClassCard key={cls.booking_id} cls={cls} index={i} />
          ))}
        </motion.div>
      </div>
    </section>
  );
}

type UpcomingClass = {
  booking_id: string;
  class_name: string;
  // Arabic alternate-language name/description from the RPC. Empty string when
  // the tenant hasn't translated that class; we fall back to the base field.
  class_name_alt: string | null;
  class_description: string | null;
  class_description_alt: string | null;
  image_url: string | null;
  trainer_name: string | null;
  start_time: string;
  end_time: string | null;
  booking_date: string;
  spots_remaining: number;
  is_full: boolean;
};

// Brand accents cycled per card so a row reads teal / purple / pink.
const CARD_ACCENTS = [
  'var(--karama-teal)',
  'var(--karama-purple)',
  'var(--karama-pink)',
];

function UpcomingClassCard({
  cls,
  index,
}: {
  cls: UpcomingClass;
  index: number;
}) {
  const { i18n } = useTranslation();
  const isAr = i18n.language?.startsWith('ar') ?? false;
  const locale = isAr ? 'ar-SA' : undefined;
  const bookingDate = new Date(cls.booking_date + 'T00:00:00');
  const now = new Date();
  const isToday = bookingDate.toDateString() === now.toDateString();
  const isTomorrow =
    bookingDate.toDateString() ===
    new Date(now.getTime() + 86_400_000).toDateString();
  const dateLabel = isToday
    ? isAr
      ? 'اليوم'
      : 'Today'
    : isTomorrow
      ? isAr
        ? 'غداً'
        : 'Tomorrow'
      : bookingDate.toLocaleDateString(locale, { weekday: 'short' });

  const formatTime = (time: string) => {
    const [h, m] = time.split(':');
    const d = new Date();
    d.setHours(Number(h), Number(m));
    return d.toLocaleTimeString(locale, {
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Duration from start/end when both are present (e.g. "60 min").
  const duration = (() => {
    if (!cls.end_time) return null;
    const toMins = (t: string) => {
      const [h, m] = t.split(':');
      return Number(h) * 60 + Number(m);
    };
    const mins = toMins(cls.end_time) - toMins(cls.start_time);
    if (mins <= 0) return null;
    return isAr ? `${mins} دقيقة` : `${mins} min`;
  })();

  const accent = CARD_ACCENTS[index % CARD_ACCENTS.length]!;

  // In Arabic, prefer the tenant's translated name/description (the RPC's
  // *_alt fields), falling back to the base field when the alt is empty/null
  // so an untranslated class still shows its English name rather than a blank.
  const displayName =
    (isAr && cls.class_name_alt?.trim()) || cls.class_name;
  const displayDescription =
    (isAr && cls.class_description_alt?.trim()) || cls.class_description;

  // Prefer the session's own DB image; otherwise show a local class photo
  // matched by class type so every card has a relevant thumbnail. DB images are
  // external (unoptimized); local fallbacks go through Next's optimizer.
  const isDbImage = Boolean(cls.image_url);
  const posterSrc = cls.image_url ?? classImageFor(cls.class_name, index);

  return (
    <Link
      href={`/event/${cls.booking_id}`}
      className="group block w-[220px] flex-shrink-0"
      style={{ scrollSnapAlign: 'start' }}
    >
      {/* Poster image, native-sized (220×280) so several cards peek at once.
          Every card shows a photo: the session's DB image when present, else a
          class-type-matched local photo. The brand-tinted backdrop only shows
          through while the image loads. */}
      <div
        className="relative h-[280px] overflow-hidden"
        style={{ background: 'var(--karama-accent)' }}
      >
        <Image
          src={posterSrc}
          alt={displayName}
          fill
          sizes="220px"
          className="object-cover transition-transform duration-500 group-hover:scale-105"
          unoptimized={isDbImage}
        />

        {/* Legibility scrim. A strong bottom-anchored gradient (heavier than
            the native one) so the white name/time read on a photo OR on the
            lighter top of a colored fallback panel. */}
        <div
          className="absolute inset-0"
          style={{
            background:
              'linear-gradient(180deg, transparent 30%, rgba(0,0,0,0.35) 62%, rgba(0,0,0,0.78) 100%)',
          }}
        />

        {/* All info on the image (native-card style): date·time, class name,
            and trainer, white over the bottom scrim. */}
        <div className="absolute right-4 bottom-4 left-4 text-white">
          <p className="font-karama-label mb-1 text-[0.6rem] text-white/90">
            {dateLabel} · {formatTime(cls.start_time)}
            {duration ? ` · ${duration}` : ''}
          </p>
          <h3 className="font-karama-heading text-xl leading-tight">
            {displayName}
          </h3>
          {cls.trainer_name && (
            <p className="mt-1 text-[0.7rem] text-white/90">
              {isAr ? 'مع' : 'with'} {cls.trainer_name}
            </p>
          )}
        </div>
      </div>

      {/* Below the image: optional description + Book CTA. */}
      <div className="mt-3">
        {displayDescription && (
          <p className="mb-2.5 line-clamp-2 text-xs leading-relaxed text-gray-500">
            {displayDescription}
          </p>
        )}
        <span
          className="font-karama-label inline-flex items-center gap-1 text-[0.6rem] transition-all group-hover:gap-1.5"
          style={{ color: accent }}
        >
          {isAr ? 'احجزوا هذا الكلاس' : 'Book This Class'}{' '}
          <ArrowRight size={11} className="rtl:-scale-x-100" />
        </span>
      </div>
    </Link>
  );
}

function GallerySection() {
  const { i18n } = useTranslation();
  const isAr = i18n.language?.startsWith('ar') ?? false;
  const tiles = [
    {
      src: IMAGES.heroMats,
      alt: 'Karama Yoga studio with mats laid out',
      className: 'col-span-1 row-span-2',
      height: 604,
      objectPosition: 'center',
      travel: 65,
    },
    {
      src: IMAGES.classWarrior,
      alt: 'Sweaty Warrior class',
      className: '',
      height: 300,
      objectPosition: 'top',
      travel: 110,
    },
    {
      src: IMAGES.heroWall,
      alt: 'Karama Yoga studio equipment wall',
      className: '',
      height: 300,
      objectPosition: 'center',
      travel: 50,
    },
    {
      src: IMAGES.heroEntrance,
      alt: 'Karama Yoga studio entrance',
      className: 'row-span-2',
      height: 604,
      objectPosition: 'center',
      travel: 80,
    },
    {
      src: IMAGES.classRestore,
      alt: 'Hatha & Restore class',
      className: '',
      height: 300,
      objectPosition: 'top',
      travel: 95,
    },
    {
      src: IMAGES.exterior,
      alt: 'Karama Yoga exterior sign',
      className: '',
      height: 300,
      objectPosition: 'center',
      travel: 58,
    },
  ];

  return (
    <section className="bg-white pt-2 pb-2">
      <div className="mx-auto mb-6 max-w-[1320px] px-6 pt-10 text-center lg:px-8">
        <motion.p
          className="font-karama-label mb-3 text-xs"
          style={{ color: 'var(--karama-pink)' }}
        >
          {isAr ? 'من داخل كراما' : 'Inside Karama'}
        </motion.p>
        <motion.h2
          transition={{ delay: 0.1 }}
          className="font-karama-heading text-[var(--karama-secondary)]"
          style={{ fontSize: 'clamp(1.8rem, 3.5vw, 2.8rem)' }}
        >
          {isAr ? 'مكان لا مثيل له' : 'A space unlike any other'}
        </motion.h2>
      </div>

      <div className="grid grid-cols-2 gap-1 lg:grid-cols-4">
        {tiles.map((tile, i) => (
          <motion.div
            key={tile.src + i}
            transition={{ duration: 0.6, delay: i * 0.06, ease: EASE_OUT }}
            className={`group relative overflow-hidden ${tile.className}`}
            style={{ height: tile.height }}
          >
            <div className="absolute inset-0 transition-transform duration-700 group-hover:scale-105">
              <ParallaxImage
                src={tile.src}
                alt={tile.alt}
                sizes="(min-width: 1024px) 25vw, 50vw"
                objectPosition={tile.objectPosition}
                travel={tile.travel}
              />
            </div>
          </motion.div>
        ))}
      </div>
    </section>
  );
}

function WhyKaramaSection() {
  const { i18n } = useTranslation();
  const isAr = i18n.language?.startsWith('ar') ?? false;
  return (
    <section className="border-b border-gray-100 bg-white py-14 lg:py-20">
      <div className="mx-auto max-w-[1320px] px-6 lg:px-8">
        <motion.div
          variants={stagger}
          className="mb-12 text-center"
        >
          <motion.p
            variants={fadeUp}
            className="font-karama-label mb-3 text-xs"
            style={{ color: 'var(--karama-purple)' }}
          >
            {isAr ? 'لماذا كراما؟' : 'Why Karama'}
          </motion.p>
          <motion.h2
            variants={fadeUp}
            className="font-karama-heading text-[var(--karama-secondary)]"
            style={{ fontSize: 'clamp(2rem, 4vw, 3.2rem)' }}
          >
            {isAr ? (
              <>
                الاستوديو الأكثر حصولاً على جوائز
                <br />
                <span style={{ color: 'var(--karama-teal)' }}>
                  في مجال اليوغا بجدة منذ عام ٢٠١٦
                </span>
              </>
            ) : (
              <>
                Jeddah&apos;s most awarded
                <br />
                <span style={{ color: 'var(--karama-teal)' }}>
                  yoga studio since 2016
                </span>
              </>
            )}
          </motion.h2>
        </motion.div>

        {/* Compact key-stats row, the page's single home for these numbers
            (replaces both the standalone stats bar and the old duplicate
            stat icon-cards). */}
        <div className="mb-14 grid grid-cols-2 divide-x divide-y divide-gray-100 border border-gray-100 sm:grid-cols-4 sm:divide-y-0">
          {stats.map((stat) => (
            <div key={stat.label.en} className="px-4 py-6 text-center">
              <p
                className="font-karama-heading mb-1"
                style={{ fontSize: '2rem', color: stat.color }}
              >
                {stat.value}
              </p>
              <p className="font-karama-label text-[0.65rem] text-gray-500">
                {pick(stat.label, isAr)}
              </p>
            </div>
          ))}
        </div>

        <div className="grid items-stretch gap-10 lg:grid-cols-2">
          <motion.div variants={stagger}>
            <motion.p
              variants={fadeUp}
              className="font-karama-label mb-2 text-xs"
              style={{ color: 'var(--karama-teal)' }}
            >
              {isAr ? 'ما يميّز كراما' : 'The Karama Difference'}
            </motion.p>
            <motion.h3
              variants={fadeUp}
              className="font-karama-heading mb-6 text-[var(--karama-secondary)]"
              style={{ fontSize: 'clamp(1.5rem, 2.6vw, 2rem)' }}
            >
              {isAr ? 'ما ستجدونه في كراما' : "What you'll find at Karama"}
            </motion.h3>
            <div className="grid gap-3 sm:grid-cols-2">
              {whatYoullFind.map((item) => {
                const Icon = item.icon;
                const tint = TINTS[item.tint];
                return (
                  <motion.div
                    key={item.title.en}
                    variants={fadeUp}
                    className="flex items-center gap-3 border p-3.5 transition-shadow duration-200 hover:shadow-sm"
                    style={{ background: tint.bg, borderColor: tint.border }}
                  >
                    <span
                      className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full"
                      style={{ background: 'white', color: tint.accent }}
                    >
                      <Icon size={16} />
                    </span>
                    <p className="text-sm leading-snug font-medium text-gray-800">
                      {pick(item.title, isAr)}
                    </p>
                  </motion.div>
                );
              })}
            </div>
          </motion.div>

          <motion.div
            transition={{ duration: 0.7 }}
            className="flex h-full flex-col p-8 lg:p-10"
            style={{
              background: 'var(--karama-accent)',
              borderLeft: '4px solid var(--karama-teal)',
            }}
          >
            <p
              className="font-karama-label mb-3 text-xs"
              style={{ color: 'var(--karama-teal)' }}
            >
              {isAr ? 'هل أنتم جدد على اليوغا؟' : 'New to Yoga?'}
            </p>
            <h3
              className="font-karama-heading mb-4 text-2xl"
              style={{ color: 'var(--karama-secondary)' }}
            >
              {isAr ? 'لقد جئتم إلى المكان الصحيح' : "You've come to the right place"}
            </h3>
            <p className="mb-3 text-sm leading-relaxed text-gray-600">
              {isAr
                ? 'سواء كان هذا أول كلاس لكم أو الألف، مدرّسونا بجانبكم في كل خطوة.'
                : "Whether it's your first class or your thousandth, our teachers are here to guide you every step of the way."}
            </p>
            <p className="mb-1 text-sm font-semibold text-gray-800">
              {isAr
                ? 'لا تحتاجون إلى خبرة سابقة. لا تحتاجون إلى مرونة.'
                : 'No experience needed. No flexibility required.'}
            </p>
            <p className="mb-5 text-sm font-semibold text-gray-800">
              {isAr
                ? 'فقط تعالوا. سنتكفّل بالباقي.'
                : "Just show up. We'll take care of the rest."}
            </p>
            {/* mt-auto pins the CTA to the card's bottom so the stretched
                card's bottom edge lines up with the left column's last row. */}
            <Link href="/book" className="k-btn-teal mt-auto self-start">
              {isAr ? 'احجزوا أول كلاس لكم' : 'Book Your First Class'}{' '}
              <ArrowRight size={14} className="rtl:-scale-x-100" />
            </Link>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

// A single Featured-In pill. Renders an anchor when the publication has a
// link (new tab + hover lift), else a static pill. `index` drives the
// alternating teal/purple accent so it stays continuous across both rows.
function MediaPill({
  item,
  index,
}: {
  item: { name: string; href?: string };
  index: number;
}) {
  // Three-colour brand rotation (teal / purple / pink).
  const accent = [
    'var(--karama-teal)',
    'var(--karama-purple)',
    'var(--karama-pink)',
  ][index % 3]!;
  const pillClass =
    'font-karama-label rounded-full border-2 px-5 py-2 text-xs font-bold tracking-[0.08em] transition-all duration-200';
  const pillStyle = {
    color: accent,
    borderColor: `color-mix(in srgb, ${accent} 45%, white)`,
    background: `color-mix(in srgb, ${accent} 6%, white)`,
  };

  if (item.href) {
    return (
      <motion.a
        href={item.href}
        target="_blank"
        rel="noopener noreferrer"
        transition={{ delay: index * 0.07 }}
        className={`${pillClass} inline-flex items-center gap-1 hover:-translate-y-0.5 hover:shadow-sm`}
        style={pillStyle}
      >
        {item.name.toUpperCase()}
        <ExternalLink className="h-3 w-3 shrink-0 opacity-60" />
      </motion.a>
    );
  }

  return (
    <motion.span
      transition={{ delay: index * 0.07 }}
      className={`${pillClass} cursor-default`}
      style={pillStyle}
    >
      {item.name.toUpperCase()}
    </motion.span>
  );
}

function FeaturedInSection() {
  const { i18n } = useTranslation();
  const isAr = i18n.language?.startsWith('ar') ?? false;
  return (
    <section className="border-b border-gray-100 bg-white py-12">
      <div className="mx-auto max-w-[1320px] px-6 lg:px-8">
        <p
          className="font-karama-label mb-7 text-center text-xs"
          style={{ color: 'var(--karama-purple)' }}
        >
          {isAr ? 'ذُكرنا في' : 'Featured In'}
        </p>
        <div className="flex flex-wrap items-center justify-center gap-3 sm:gap-4">
          {MEDIA.map((m, i) => (
            <MediaPill key={m.name} item={m} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
}

function PhilosophyStrip() {
  const { i18n } = useTranslation();
  const isAr = i18n.language?.startsWith('ar') ?? false;
  return (
    <section className="py-12" style={{ background: 'var(--karama-teal)' }}>
      <div className="mx-auto max-w-[1320px] px-6 lg:px-8">
        <div className="grid gap-8 text-center text-white sm:grid-cols-3">
          {philosophy.map((item, i) => (
            <motion.div
              key={item.title.en}
              transition={{ duration: 0.5, delay: i * 0.1 }}
            >
              <p className="font-karama-label mb-2 text-xs text-white/75">
                {pick(item.title, isAr)}
              </p>
              <p className="whitespace-pre-line text-sm leading-relaxed text-white/95">
                {pick(item.body, isAr)}
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

function AboutSection() {
  const { i18n } = useTranslation();
  const isAr = i18n.language?.startsWith('ar') ?? false;
  return (
    <section id="about" className="bg-white py-16 lg:py-24">
      <div className="mx-auto max-w-[1320px] px-6 lg:px-8">
        <div className="grid items-center gap-12 lg:grid-cols-2 lg:gap-20">
          <motion.div
            transition={{ duration: 0.7 }}
            className="relative"
          >
            <div className="relative" style={{ height: '680px' }}>
              <ParallaxImage
                src={IMAGES.about}
                alt="Lana Nazer in the Karama boutique"
                sizes="(min-width: 1024px) 50vw, 100vw"
                objectPosition="top"
                travel={80}
              />
            </div>
            <div
              className="absolute -right-5 -bottom-5 bg-white p-5 shadow-xl"
              style={{ border: '2px solid var(--karama-purple)' }}
            >
              <Award
                size={20}
                style={{ color: 'var(--karama-purple)' }}
                className="mb-1"
              />
              <p className="text-xs leading-tight font-bold text-gray-800">
                {isAr ? (
                  <>
                    أفضل استوديو يوغا
                    <br />
                    في المملكة العربية السعودية
                  </>
                ) : (
                  <>
                    Best Yoga Studio
                    <br />
                    Saudi Arabia
                  </>
                )}
              </p>
              <p className="mt-0.5 text-xs text-gray-400">2020–2026</p>
            </div>
          </motion.div>

          <motion.div
            variants={stagger}
          >
            <motion.p
              variants={fadeUp}
              className="font-karama-label mb-4 text-xs"
              style={{ color: 'var(--karama-teal)' }}
            >
              {isAr ? 'من نحن' : 'Who We Are'}
            </motion.p>
            <motion.h2
              variants={fadeUp}
              className="font-karama-heading mb-6 text-[var(--karama-secondary)]"
              style={{ fontSize: 'clamp(2rem, 4vw, 3.2rem)' }}
            >
              {isAr ? (
                <>كراما ليس مجرد استوديو يوغا</>
              ) : (
                <>
                  Karama is not just
                  <br />a yoga studio
                </>
              )}
            </motion.h2>
            <motion.p
              variants={fadeUp}
              className="mb-5 text-base leading-relaxed text-[var(--karama-muted)]"
            >
              {isAr
                ? 'كراما فضاء ثقافي للصحة والعافية، حركة عصرية متجذّرة في الثقافة تُعيد تعريف اليوغا في المملكة العربية السعودية.'
                : 'Karama is a cultural wellness space, a modern, high-energy, culturally-rooted wellness movement redefining yoga in Saudi Arabia.'}
            </motion.p>
            <motion.p
              variants={fadeUp}
              className="mb-6 text-base leading-relaxed text-[var(--karama-muted)]"
            >
              {isAr
                ? 'تأسّست في جدة عام ٢٠١٦ على يد لنا ناظر، أُسِّست كراما بنيّة واضحة: جعل الصحة والعافية في متناول الجميع، قويةً ومحوّلة للحياة، حتى في أكثر الأوقات ازدحاماً.'
                : 'Founded in Jeddah in 2016 by Lana Nazer, Karama was built with one clear intention: to make wellness accessible, powerful, and deeply transformative, even in the busiest lives.'}
            </motion.p>
            <motion.div
              variants={fadeUp}
              className="mb-6 p-6"
              style={{
                background: 'var(--karama-accent)',
                borderLeft: '3px solid var(--karama-teal)',
              }}
            >
              <p
                className="font-karama-label mb-2 text-xs"
                style={{ color: 'var(--karama-teal)' }}
              >
                {isAr ? 'وعدنا' : 'Our Promise'}
              </p>
              <p className="text-sm leading-relaxed text-gray-600">
                {isAr
                  ? 'نسعى إلى خلق مكان تشعرون فيه بالترحيب والدعم والأمان، لتعيشوا حياةً أكثر صحةً، بجسد أقوى، وذهن أكثر هدوءاً، وعلاقات أعمق.'
                  : 'To create a space where wellness feels welcoming, approachable, accessible, and non-intimidating, so you feel supported in living a healthier life, feeling stronger in your body, calmer in your mind, and more connected to yourself and the world around you.'}
              </p>
            </motion.div>
            <motion.div variants={fadeUp} className="flex flex-wrap gap-4">
              <Link href="/book" className="k-btn-teal">
                {isAr ? 'انضموا إلى الحركة' : 'Join the Movement'}
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

function FounderSection() {
  const { i18n } = useTranslation();
  const isAr = i18n.language?.startsWith('ar') ?? false;
  return (
    <section
      id="founder"
      className="py-16 lg:py-24"
      style={{ background: 'var(--karama-accent)' }}
    >
      <div className="mx-auto max-w-[1320px] px-6 lg:px-8">
        <div className="grid items-center gap-12 lg:grid-cols-2 lg:gap-20">
          <motion.div
            variants={stagger}
            className="order-2 lg:order-1"
          >
            <motion.p
              variants={fadeUp}
              className="font-karama-label mb-4 text-xs"
              style={{ color: 'var(--karama-purple)' }}
            >
              {isAr ? 'المرأة التي أسّست كراما' : 'The Woman Behind Karama'}
            </motion.p>
            <motion.h2
              variants={fadeUp}
              className="font-karama-heading mb-2 text-[var(--karama-secondary)]"
              style={{ fontSize: 'clamp(2rem, 4vw, 3.2rem)' }}
            >
              {isAr ? 'لنا ناظر' : 'Lana Nazer'}
            </motion.h2>
            <motion.p
              variants={fadeUp}
              className="font-karama-label mb-6 text-xs"
              style={{ color: 'var(--karama-teal)' }}
            >
              {isAr
                ? 'مؤسِّسة · مدربة يوغا حاصلة على جوائز · رائدة في مجال الصحة والعافية'
                : 'Founder · Award-winning Yoga Teacher · Wellness Pioneer'}
            </motion.p>
            <motion.p
              variants={fadeUp}
              className="mb-4 text-base leading-relaxed text-[var(--karama-muted)]"
            >
              {isAr
                ? 'مؤسِّسة كراما يوغا وإحدى أبرز الأصوات التي تُشكّل مفهوم الصحة الحديث في المملكة. بعد أن تدرّبت على أكثر من ١٢ تخصصاً في اليوغا وعلّمت حول العالم، عادت لنا إلى وطنها بحلم: مكان تشعرون فيه بقوة الصحة وترحيبها وانتمائها الثقافي وإتاحتها للجميع.'
                : 'Founder of Karama Yoga and one of the leading voices shaping modern wellness in Saudi Arabia. After training in over 12 yoga disciplines and teaching across the globe, Lana returned home with a vision: a space where wellness feels powerful, welcoming, culturally relevant, and accessible to all.'}
            </motion.p>
            <motion.p
              variants={fadeUp}
              className="mb-8 text-base leading-relaxed text-[var(--karama-muted)]"
            >
              {isAr
                ? 'ما بدأ في عام ٢٠١٦ أصبح اليوم من أكثر مجتمعات الصحة حصولاً على جوائز في المملكة، وقد أثّر في آلاف الأشخاص، وأعاد رسم صورة اليوغا في المملكة العربية السعودية.'
                : "What began in 2016 has grown into one of the Kingdom's most awarded wellness communities, impacting thousands of lives and redefining what yoga can look and feel like in Saudi Arabia."}
            </motion.p>

            <motion.div
              variants={fadeUp}
              className="mb-8 grid grid-cols-3 gap-4"
            >
              {[
                { value: '10+', label: { en: 'Years Teaching', ar: 'سنوات تدريس' } },
                { value: '13K+', label: { en: 'Lives Impacted', ar: 'ألف حياة تغيّرت' } },
                { value: '6×', label: { en: 'Prestige Award', ar: 'جائزة بريستيج' } },
              ].map((s) => (
                <div key={s.label.en} className="bg-white p-4 text-center">
                  <p
                    className="font-karama-heading mb-1 text-2xl"
                    style={{ color: 'var(--karama-purple)' }}
                  >
                    {s.value}
                  </p>
                  <p className="font-karama-label text-[0.6rem] text-gray-500">
                    {pick(s.label, isAr)}
                  </p>
                </div>
              ))}
            </motion.div>

          </motion.div>

          <motion.div
            transition={{ duration: 0.7 }}
            className="order-1 grid grid-cols-2 gap-4 lg:order-2"
          >
            <div className="relative" style={{ height: '560px' }}>
              <ParallaxImage
                src={IMAGES.founder}
                alt="Lana Nazer, founder of Karama Yoga"
                sizes="(min-width: 1024px) 25vw, 50vw"
                objectPosition="top"
                travel={75}
              />
            </div>
            <div className="flex flex-col gap-4">
              <div className="relative" style={{ height: '270px' }}>
                <ParallaxImage
                  src={IMAGES.founder2}
                  alt="Lana Nazer"
                  sizes="(min-width: 1024px) 25vw, 50vw"
                  objectPosition="center 35%"
                  travel={58}
                />
              </div>
              <div
                className="flex flex-1 items-center justify-center p-6"
                style={{ background: 'var(--karama-purple)' }}
              >
                <div className="text-center text-white">
                  <p className="font-karama-heading mb-1 text-3xl">6×</p>
                  <p className="font-karama-label text-xs text-white/90">
                    {isAr ? (
                      <>
                        حائزة على جائزة
                        <br />
                        بريستيج
                      </>
                    ) : (
                      <>
                        Prestige Award
                        <br />
                        Winner
                      </>
                    )}
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

function RetreatsSection() {
  const { i18n } = useTranslation();
  const isAr = i18n.language?.startsWith('ar') ?? false;
  return (
    <section className="border-t border-gray-100 bg-white py-14">
      <div className="mx-auto max-w-[1320px] px-6 lg:px-8">
        <div className="grid items-center gap-10 lg:grid-cols-2">
          <motion.div
            variants={stagger}
          >
            <motion.p
              variants={fadeUp}
              className="font-karama-label mb-3 text-xs"
              style={{ color: 'var(--karama-teal)' }}
            >
              {isAr ? 'الرحلات والتجارب' : 'Retreats & Experiences'}
            </motion.p>
            <motion.h2
              variants={fadeUp}
              className="font-karama-heading mb-4 text-[var(--karama-secondary)]"
              style={{ fontSize: 'clamp(1.8rem, 3.5vw, 2.8rem)' }}
            >
              {isAr ? 'خارج الاستوديو' : 'Beyond the studio'}
            </motion.h2>
            <motion.p
              variants={fadeUp}
              className="mb-4 text-base leading-relaxed text-[var(--karama-muted)]"
            >
              {isAr
                ? 'منذ سنوات، تُصمّم كراما يوغا رحلات صحية وعطلاً مميزة وتجارب في وجهات متنوعة داخل المملكة العربية السعودية وخارجها، من بينها سريلانكا والعُلا.'
                : 'For years, Karama Yoga has been creating retreats, wellness weekends, and destination experiences across Saudi Arabia and beyond, including Sri Lanka and AlUla.'}
            </motion.p>
            <motion.p
              variants={fadeUp}
              className="mb-6 text-sm text-gray-500 italic"
            >
              {isAr
                ? 'قريباً... تجربة جديدة بانتظاركم. اضغطوا أدناه للحصول على أولوية الوصول إلى الرحلات والتجارب القادمة.'
                : 'Something exciting is coming. Tap below for priority access to upcoming retreats and experiences.'}
            </motion.p>
            <motion.div variants={fadeUp}>
              <a
                href={WHATSAPP_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="k-btn-teal"
              >
                <MessageCircle size={14} />{' '}
                {isAr
                  ? 'احصلوا على أولوية الوصول عبر واتساب'
                  : 'Get Priority Access on WhatsApp'}
              </a>
            </motion.div>
          </motion.div>
          <motion.div
            transition={{ duration: 0.7 }}
            className="relative overflow-hidden"
            style={{ height: '400px' }}
          >
            <ParallaxImage
              src={IMAGES.boutique}
              alt="Karama Yoga retreat"
              sizes="(min-width: 1024px) 50vw, 100vw"
              travel={60}
            />
          </motion.div>
        </div>
      </div>
    </section>
  );
}

function ThisMonthSection() {
  const { i18n } = useTranslation();
  const isAr = i18n.language?.startsWith('ar') ?? false;
  return (
    <section
      id="this-month"
      className="border-t border-gray-100 bg-white py-14"
    >
      <div className="mx-auto max-w-[1320px] px-6 lg:px-8">
        <motion.div
          variants={stagger}
          className="mb-8"
        >
          <motion.p
            variants={fadeUp}
            className="font-karama-label mb-3 text-xs"
            style={{ color: 'var(--karama-purple)' }}
          >
            {isAr ? 'هذا الشهر في كراما' : 'This Month at Karama'}
          </motion.p>
          <motion.h2
            variants={fadeUp}
            className="font-karama-heading text-[var(--karama-secondary)]"
            style={{ fontSize: 'clamp(1.8rem, 3.5vw, 2.8rem)' }}
          >
            {isAr ? 'ماذا يحدث؟' : "What's happening"}
          </motion.h2>
        </motion.div>
        <div className="grid gap-6 sm:grid-cols-3">
          {thisMonth.map((item, i) => {
            const Icon = item.icon;
            return (
              <motion.div
                key={item.label.en}
                transition={{ delay: i * 0.1 }}
                className="border border-gray-100 p-6 transition-all duration-200 hover:border-gray-200 hover:shadow-sm"
              >
                <Icon
                  size={22}
                  style={{ color: 'var(--karama-teal)' }}
                  className="mb-3"
                />
                <h3
                  className="mb-2 text-base font-bold"
                  style={{ color: 'var(--karama-secondary)' }}
                >
                  {pick(item.label, isAr)}
                </h3>
                <p className="text-sm text-gray-500">{pick(item.desc, isAr)}</p>
              </motion.div>
            );
          })}
        </div>
        <div className="mt-6 text-center">
          <a
            href={WHATSAPP_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="k-btn-outline"
          >
            <MessageCircle size={14} />{' '}
            {isAr ? 'استفسروا عن الفعاليات القادمة' : 'Ask About Upcoming Events'}
          </a>
        </div>
      </div>
    </section>
  );
}

function TestimonialsSection() {
  const { i18n } = useTranslation();
  const isAr = i18n.language?.startsWith('ar') ?? false;
  return (
    <section id="reviews" className="py-16 lg:py-24" style={{ background: 'var(--karama-accent)' }}>
      <div className="mx-auto max-w-[1320px] px-6 lg:px-8">
        <motion.div
          variants={stagger}
          className="mb-12 text-center"
        >
          <motion.p
            variants={fadeUp}
            className="font-karama-label mb-3 text-xs"
            style={{ color: 'var(--karama-pink)' }}
          >
            {isAr ? 'آراء العملاء' : 'What People Feel'}
          </motion.p>
          <motion.h2
            variants={fadeUp}
            className="font-karama-heading text-[var(--karama-secondary)]"
            style={{ fontSize: 'clamp(2rem, 4vw, 3.2rem)' }}
          >
            {isAr
              ? 'ينضم الناس لأنهم يشعرون بشيء ما'
              : 'People join because they feel something'}
          </motion.h2>
        </motion.div>

        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {testimonials.map((t, i) => (
            <motion.div
              key={t.name.en}
              transition={{ duration: 0.5, delay: i * 0.1 }}
              className="border border-gray-100 bg-white p-6 transition-all duration-200 hover:border-gray-200 hover:shadow-md"
            >
              <div className="mb-4 flex gap-0.5">
                {[0, 1, 2, 3, 4].map((j) => (
                  <Star
                    key={j}
                    size={12}
                    style={{ color: t.color, fill: t.color }}
                    stroke="none"
                  />
                ))}
              </div>
              <p className="mb-5 text-sm leading-relaxed text-gray-600 italic">
                &ldquo;{pick(t.quote, isAr)}&rdquo;
              </p>
              <div>
                <p className="text-sm font-bold text-gray-900">
                  {pick(t.name, isAr)}
                </p>
                <p className="text-xs text-gray-400">{pick(t.role, isAr)}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

function TeacherTrainingSection() {
  const { i18n } = useTranslation();
  const isAr = i18n.language?.startsWith('ar') ?? false;
  return (
    <section
      id="teacher-training"
      className="border-t border-gray-100 bg-white py-14"
    >
      <div className="mx-auto max-w-[1320px] px-6 lg:px-8">
        <div className="grid items-center gap-10 lg:grid-cols-2">
          <motion.div
            variants={stagger}
          >
            <motion.p
              variants={fadeUp}
              className="font-karama-label mb-3 text-xs"
              style={{ color: 'var(--karama-purple)' }}
            >
              {isAr ? 'تدريب المدربين' : 'Teacher Training'}
            </motion.p>
            <motion.h2
              variants={fadeUp}
              className="font-karama-heading mb-4 text-[var(--karama-secondary)]"
              style={{ fontSize: 'clamp(1.8rem, 3.5vw, 2.8rem)' }}
            >
              {isAr ? (
                <>أصبحوا مدربين معتمدين في اليوغا</>
              ) : (
                <>
                  Become a certified
                  <br />
                  yoga teacher
                </>
              )}
            </motion.h2>
            <motion.p
              variants={fadeUp}
              className="mb-6 text-base leading-relaxed text-[var(--karama-muted)]"
            >
              {isAr
                ? 'تدرّبوا مع مدربي اليوغا ذوي المستوى العالمي واحصلوا على شهاداتكم. صُمّمت برامج تدريب المدربين لدينا لمن يرغب في تعميق ممارسته ومشاركة فضل اليوغا مع الآخرين.'
                : "Train with Karama's world-class instructors and earn your certification. Our teacher training programs are designed for those who want to deepen their practice and share the gift of yoga with others."}
            </motion.p>
            <motion.div variants={fadeUp}>
              <a
                href={WHATSAPP_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="k-btn-purple"
              >
                <MessageCircle size={14} />{' '}
                {isAr ? 'استفسروا عبر واتساب' : 'Enquire on WhatsApp'}
              </a>
            </motion.div>
          </motion.div>
          <motion.div
            transition={{ duration: 0.7 }}
            className="relative overflow-hidden"
            style={{ height: '400px' }}
          >
            <ParallaxImage
              src={IMAGES.heroWall}
              alt="Teacher Training at Karama Yoga"
              sizes="(min-width: 1024px) 50vw, 100vw"
              travel={60}
            />
          </motion.div>
        </div>
      </div>
    </section>
  );
}

function CorporateSection() {
  const { i18n } = useTranslation();
  const isAr = i18n.language?.startsWith('ar') ?? false;
  return (
    <section
      id="corporate"
      className="py-16 lg:py-24"
      style={{ background: 'var(--karama-accent)' }}
    >
      <div className="mx-auto max-w-[1320px] px-6 lg:px-8">
        <motion.div
          variants={stagger}
          className="mb-12"
        >
          <motion.p
            variants={fadeUp}
            className="font-karama-label mb-3 text-xs"
            style={{ color: 'var(--karama-teal)' }}
          >
            {isAr ? 'الشركات والشراكات' : 'Corporate & Collaborations'}
          </motion.p>
          <motion.h2
            variants={fadeUp}
            className="font-karama-heading mb-4 text-[var(--karama-secondary)]"
            style={{ fontSize: 'clamp(2rem, 4vw, 3.2rem)' }}
          >
            {isAr ? (
              <>تجارب صحة وعافية تجمع الناس</>
            ) : (
              <>
                Wellness experiences that
                <br />
                <span style={{ color: 'var(--karama-teal)' }}>
                  bring people together
                </span>
              </>
            )}
          </motion.h2>
          <motion.p
            variants={fadeUp}
            className="max-w-2xl text-base leading-relaxed text-[var(--karama-muted)]"
          >
            {isAr
              ? 'من كلاسات الفرق الصغيرة إلى الفعاليات الكبرى، نتعاون مع الشركات والعلامات التجارية والفنادق والمنتجعات والمنظمات لتصميم تجارب عافية مدروسة تمنح المشاركين شعوراً بالطاقة والتواصل والإلهام.'
              : 'From intimate team sessions to large-scale activations, we partner with companies, brands, hotels, resorts, and organizations to create thoughtful wellness experiences that leave people feeling energized, connected, and inspired.'}
          </motion.p>
        </motion.div>

        <div className="mb-12 grid gap-6 lg:grid-cols-3">
          {corporate.map((item, i) => {
            const Icon = item.icon;
            return (
              <motion.div
                key={item.title.en}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                className="border border-gray-100 bg-white p-7 transition-all duration-200 hover:shadow-md"
              >
                <Icon
                  size={22}
                  style={{ color: item.color }}
                  className="mb-4"
                />
                <h3
                  className="font-karama-heading mb-3 text-xl"
                  style={{ color: 'var(--karama-secondary)' }}
                >
                  {pick(item.title, isAr)}
                </h3>
                <p className="mb-4 text-sm leading-relaxed text-gray-500">
                  {pick(item.desc, isAr)}
                </p>
                <ul className="space-y-1.5">
                  {item.items.map((li) => (
                    <li
                      key={li.en}
                      className="flex items-center gap-2 text-xs text-gray-600"
                    >
                      <span
                        className="h-1.5 w-1.5 flex-shrink-0 rounded-full"
                        style={{ background: item.color }}
                      />
                      {pick(li, isAr)}
                    </li>
                  ))}
                </ul>
              </motion.div>
            );
          })}
        </div>

        <motion.div
          className="border border-gray-100 bg-white p-8 text-center lg:p-12"
        >
          <h3
            className="font-karama-heading mb-3 text-2xl"
            style={{ color: 'var(--karama-secondary)' }}
          >
            {isAr ? 'لنصنع معاً شيئاً ذا معنى' : "Let's create something meaningful together"}
          </h3>
          <p className="mx-auto mb-6 max-w-xl text-sm text-gray-500">
            {isAr
              ? 'سواء كنتم تخططون لمبادرة صحة شركات أو تفعيل علامة تجارية أو رحلة أو فعالية خاصة، يسعدنا سماع أفكاركم.'
              : "Whether you're planning a corporate wellness initiative, a brand activation, a retreat, or a special event, we'd love to hear from you."}
          </p>
          <a
            href={WHATSAPP_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="k-btn-teal"
          >
            <MessageCircle size={14} />{' '}
            {isAr ? 'تواصلوا معنا عبر واتساب' : 'Get in Touch on WhatsApp'}
          </a>
        </motion.div>
      </div>
    </section>
  );
}

function CtaStrip() {
  const { i18n } = useTranslation();
  const isAr = i18n.language?.startsWith('ar') ?? false;
  return (
    <section
      className="py-16 lg:py-20"
      style={{ background: 'var(--karama-purple)' }}
    >
      <div className="mx-auto max-w-[1320px] px-6 lg:px-8">
        <motion.div
          variants={stagger}
          className="flex flex-col items-center justify-between gap-8 lg:flex-row"
        >
          <motion.div variants={fadeUp}>
            <h2
              className="font-karama-heading text-white"
              style={{ fontSize: 'clamp(1.8rem, 3.5vw, 2.8rem)' }}
            >
              {isAr ? 'اشعروا بشيء اليوم' : 'Feel something today'}
            </h2>
          </motion.div>
          <motion.div variants={fadeUp} className="flex flex-wrap gap-4">
            <Link
              href="/book"
              className="k-btn-white"
              style={{ color: 'var(--karama-purple)' }}
            >
              {isAr ? 'احجزوا كلاسكم' : 'Book Your Class'}{' '}
              <ChevronRight size={14} className="rtl:-scale-x-100" />
            </Link>
            <a
              href={WHATSAPP_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="k-btn-outline"
              style={{ color: 'white', borderColor: 'rgba(255,255,255,0.6)' }}
            >
              <MessageCircle size={14} /> {isAr ? 'تواصلوا معنا عبر واتساب' : 'WhatsApp Us'}
            </a>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}

function LocationSection() {
  const { i18n } = useTranslation();
  const isAr = i18n.language?.startsWith('ar') ?? false;
  return (
    <section id="location" className="border-t border-gray-100 bg-white py-14">
      <div className="mx-auto max-w-[1320px] px-6 lg:px-8">
        <motion.div
          variants={stagger}
          className="mb-8"
        >
          <motion.p
            variants={fadeUp}
            className="font-karama-label mb-2 text-xs"
            style={{ color: 'var(--karama-teal)' }}
          >
            {isAr ? 'الموقع' : 'Location'}
          </motion.p>
          <motion.h2
            variants={fadeUp}
            className="font-karama-heading text-[var(--karama-secondary)]"
            style={{ fontSize: 'clamp(1.8rem, 3.5vw, 2.4rem)' }}
          >
            {isAr ? 'ابحثوا عنا في جدة' : 'Find us in Jeddah'}
          </motion.h2>
        </motion.div>
        <div className="grid gap-6 lg:grid-cols-3">
          <a
            href={MAPS_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="group block border border-gray-100 p-6 transition-all duration-200 hover:border-gray-200 hover:shadow-sm"
          >
            <MapPin
              size={20}
              style={{ color: 'var(--karama-purple)' }}
              className="mb-3"
            />
            <h4 className="mb-1 text-sm font-bold">
              {isAr ? 'العنوان' : 'Address'}
            </h4>
            <p className="text-sm text-gray-500">
              {isAr
                ? '٧٧٥٠ شارع حراء، حي الشاطئ، جدة ٢٣٥١٤، المملكة العربية السعودية'
                : '7750 Hira St, Ash Shati, Jeddah 23514, Saudi Arabia'}
            </p>
            <span
              className="mt-2 inline-flex items-center gap-1.5 text-sm font-semibold transition-all group-hover:gap-2"
              style={{ color: 'var(--karama-purple)' }}
            >
              {isAr ? 'احصلوا على الاتجاهات' : 'Get Directions'}{' '}
              <ExternalLink size={12} />
            </span>
          </a>
          <div className="border border-gray-100 p-6">
            <Clock
              size={20}
              style={{ color: 'var(--karama-teal)' }}
              className="mb-3"
            />
            <h4 className="mb-3 text-sm font-bold">
              {isAr ? 'ساعات العمل' : 'Opening Hours'}
            </h4>
            <div className="space-y-2 text-sm">
              <div className="flex items-start justify-between gap-4">
                <span className="font-medium text-gray-700">
                  {isAr ? 'الأحد – الخميس' : 'Sun – Thu'}
                </span>
                <span className="text-end text-gray-500">
                  {isAr ? '١٠:٠٠ ص – ١:٠٠ م' : '10:00 AM – 1:00 PM'}
                  <br />
                  {isAr ? '٤:٠٠ – ١٠:٠٠ م' : '4:00 – 10:00 PM'}
                </span>
              </div>
              <div className="flex items-start justify-between gap-4">
                <span className="font-medium text-gray-700">
                  {isAr ? 'الجمعة' : 'Fri'}
                </span>
                <span className="text-end text-gray-500">
                  {isAr ? '١٠:٠٠ ص – ١٢:٠٠ م' : '10:00 AM – 12:00 PM'}
                  <br />
                  {isAr ? '٦:٠٠ – ١٠:٠٠ م' : '6:00 – 10:00 PM'}
                </span>
              </div>
              <div className="flex items-start justify-between gap-4">
                <span className="font-medium text-gray-700">
                  {isAr ? 'السبت' : 'Sat'}
                </span>
                <span className="text-end text-gray-500">
                  {isAr ? '١٠:٠٠ ص – ١٠:٠٠ م' : '10:00 AM – 10:00 PM'}
                </span>
              </div>
            </div>
          </div>
          <div className="border border-gray-100 p-6">
            <Star
              size={20}
              style={{ color: 'var(--karama-purple)' }}
              className="mb-3"
            />
            <h4 className="mb-2 text-sm font-bold">
              {isAr ? 'تواصلوا معنا' : 'Get in Touch'}
            </h4>
            <a
              href={WHATSAPP_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 text-sm font-semibold"
              style={{ color: 'var(--karama-purple)' }}
            >
              {isAr ? 'راسلونا عبر واتساب' : 'WhatsApp Us'}{' '}
              <ExternalLink size={12} />
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}

function FloatingWhatsApp() {
  return (
    <a
      href={WHATSAPP_URL}
      target="_blank"
      rel="noopener noreferrer"
      aria-label="WhatsApp Karama Yoga"
      className="fixed right-6 bottom-6 z-50 flex h-14 w-14 items-center justify-center rounded-full shadow-lg transition-all duration-200 hover:-translate-y-0.5 hover:shadow-xl active:scale-95"
      style={{ background: 'var(--karama-success)' }}
    >
      <MessageCircle size={22} className="text-white" />
    </a>
  );
}

function ScrollProgressBar() {
  const { scrollYProgress } = useScroll();
  const scaleX = useSpring(scrollYProgress, {
    stiffness: 120,
    damping: 30,
    restDelta: 0.001,
  });

  return (
    <motion.div
      aria-hidden
      className="fixed inset-x-0 top-0 z-[60] h-[3px] origin-left"
      style={{
        scaleX,
        background:
          'linear-gradient(90deg, var(--karama-teal) 0%, var(--karama-purple) 50%, var(--karama-pink) 100%)',
      }}
    />
  );
}

export default function KaramaLandingPage({ tenant }: LandingPageProps) {
  return (
    <main className="bg-white">
      <ScrollProgressBar />
      <HeroSection />
      {tenant?.slug && <ScheduleSection slug={tenant.slug} />}
      <GallerySection />
      <WhyKaramaSection />
      <FeaturedInSection />
      <PricingSection />
      <PhilosophyStrip />
      <AboutSection />
      <FounderSection />
      <RetreatsSection />
      <ThisMonthSection />
      <TestimonialsSection />
      <TeacherTrainingSection />
      <CorporateSection />
      <CtaStrip />
      <LocationSection />
      <FloatingWhatsApp />
    </main>
  );
}
